import { connect } from 'react-redux';

import isNil from 'lodash/isNil';
import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
/** import third-party libraries in the section above, keep the ascending order */

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, keep the ascending order */

import { SimpleSelect } from 'widgets-v6/select';
import { TextField } from 'widgets-v6/standard-input';

import FormField from 'widgets-v5/form-field';
import Spacer from 'widgets-v5/spacer';
import TextFieldFormatter from 'widgets-v6/text-field-formatter';
/** import widgets components in the section above, keep the ascending order */

import { getEnvironmentSettings } from 'services/environment';
import { isInternalUser } from 'states/profile/business-rules';
import {
    SpendingCycleIntervalOptions,
    SpendingCycleIntervals,
} from 'common/constants/organization-spending-cycle';

import BillingTiers from '../billing-tiers';
import flags from 'containers/flags/service';
import selector from './selector';
import TierCalculationStatus from './tier-calculation-status';
/** import classes and others in the section above, keep the ascending order */

const useStyles = makeStyles(() => ({
    red: {
        color: 'red',
    },
    feesTable: {
        '& > thead > tr > th': {
            border: 'none',
            verticalAlign: 'top',
        },
        '& > tbody > tr > td': {
            border: 'none',
            verticalAlign: 'top',
        },
    },
}));

const Fees = ({
    draft,
    onChange,
    errors,
    showErrors,
    canEditRestrictedFields,
    mode,
    relationship,
    onAddBillingTier,
    onDeleteBillingTier,
    onEditBillingTierMax,
    onEditBillingTierFee,
}) => {
    const environmentSettings = getEnvironmentSettings();
    const classes = useStyles();

    const isEditingSelf = mode === 'edit' && relationship === 'self';

    const useSpendingCycle = flags.isEnabled('efb_392_annually_spend_tiers');

    // Default values based on interval
    const spendingCycle = draft.spendingCycle || {
        interval: SpendingCycleIntervals.monthly,
        annuallyStartDay: null,
        annuallyStartMonth: null,
    };
    const { interval, annuallyStartDay, annuallyStartMonth } = spendingCycle;

    const handleSpendingCycleIntervalChange = value => {
        const updatedSpendingCycle = { ...spendingCycle, interval: value };

        if (value === SpendingCycleIntervals.ANNUALLY) {
            // Set default to January 1st (month: 1, day: 1)
            updatedSpendingCycle.annuallyStartMonth = 1;
            updatedSpendingCycle.annuallyStartDay = 1;
        } else {
            // Clear values for monthly
            updatedSpendingCycle.annuallyStartMonth = null;
            updatedSpendingCycle.annuallyStartDay = null;
        }

        onChange('spendingCycle', updatedSpendingCycle);
    };

    const handleSpendingCycleStartDateChange = date => {
        const updatedSpendingCycle = { ...spendingCycle };

        if (date) {
            // Extract month (1-12) and day (1-31) from the date
            updatedSpendingCycle.annuallyStartMonth = date.month() + 1; // moment months are 0-based, we want 1-based
            updatedSpendingCycle.annuallyStartDay = date.date();
        } else {
            updatedSpendingCycle.annuallyStartMonth = null;
            updatedSpendingCycle.annuallyStartDay = null;
        }

        onChange('spendingCycle', updatedSpendingCycle);
    };

    // Helper function to create a moment date from annuallyStartMonth and annuallyStartDay for the DatePicker
    const getDatePickerValue = () => {
        if (annuallyStartMonth && annuallyStartDay) {
            // Create a moment date with current year, specified month (1-based), and day
            return moment()
                .year(moment().year())
                .month(annuallyStartMonth - 1)
                .date(annuallyStartDay);
        }
        return null;
    };

    return (
        <div>
            {useSpendingCycle && (
                <Box mb={3}>
                    <Typography gutterBottom variant="h6">
                        Spending Cycle
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        How often the spending tiers for platform fees reset, for both Private Deals
                        and Public Inventory.
                    </Typography>
                    <Spacer type="small" />
                    <Box mb={3} sx={{ maxWidth: '50%' }}>
                        <FormLabel
                            required
                            classes={{
                                asterisk: classes.red,
                            }}
                        >
                            Spending Cycle Interval
                        </FormLabel>
                        <FormField isRequired={true}>
                            <SimpleSelect
                                value={interval}
                                onChange={e => handleSpendingCycleIntervalChange(e.target.value)}
                                options={SpendingCycleIntervalOptions}
                                placeholder="Select interval"
                                disabled={!canEditRestrictedFields}
                                error={showErrors && errors.spendingCycle?.interval}
                            />
                        </FormField>
                    </Box>
                    {interval === SpendingCycleIntervals.ANNUALLY && (
                        <Box mb={3} sx={{ maxWidth: '50%' }}>
                            <FormLabel
                                required
                                classes={{
                                    asterisk: classes.red,
                                }}
                            >
                                Spending Cycle Start Date
                            </FormLabel>
                            <Typography variant="body2" color="textSecondary">
                                Day of the year when the new spending cycle starts.
                            </Typography>
                            <Spacer type="small" />
                            <FormField isRequired={true}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        views={['month', 'day']}
                                        openTo="year"
                                        format="DD/MMMM"
                                        value={getDatePickerValue()}
                                        onChange={handleSpendingCycleStartDateChange}
                                        disabled={!canEditRestrictedFields}
                                        slotProps={{
                                            textField: {
                                                placeholder: 'Select month and day',
                                                error:
                                                    showErrors &&
                                                    (errors.spendingCycle?.annuallyStartMonth ||
                                                        errors.spendingCycle?.annuallyStartDay),
                                                helperText:
                                                    showErrors &&
                                                    (errors.spendingCycle?.annuallyStartMonth ||
                                                        errors.spendingCycle?.annuallyStartDay),
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormField>
                        </Box>
                    )}
                </Box>
            )}
            <Box mb={3}>
                <Typography gutterBottom variant="h6">
                    Private Deals Fees
                </Typography>
                {!isEditingSelf && (
                    <Box mb={3}>
                        <FormLabel
                            required
                            classes={{
                                asterisk: classes.red,
                            }}
                        >
                            Infrastructure Cost
                        </FormLabel>
                        <FormField isRequired={true}>
                            <TextFieldFormatter
                                initialValue={draft.privateDealInfraCost}
                                onChange={value => onChange('privateDealInfraCost', value)}
                                shouldAllowChange={v => !/[a-zA-Z]/.test(v)}
                                formatIn={v => {
                                    return isNil(v) ? '' : numeral(v).format('0,0.00');
                                }}
                                formatOut={v => {
                                    return v ? numeral(v).value() : 0;
                                }}
                                renderInput={({ value, onChange }) => (
                                    <TextField
                                        value={value}
                                        onChange={onChange}
                                        required
                                        disabled={!canEditRestrictedFields}
                                        helperText={showErrors && errors.privateDealInfraCost}
                                        error={showErrors && errors.privateDealInfraCost}
                                        endAdornment="CPM"
                                        startAdornment={environmentSettings.getCurrencySign()}
                                    />
                                )}
                            />
                        </FormField>
                    </Box>
                )}
                <Box>
                    <FormLabel
                        required
                        classes={{
                            asterisk: classes.red,
                        }}
                    >
                        Tiers
                    </FormLabel>
                    <BillingTiers
                        tiers={draft.privateInventorySpendTiers}
                        onAddBillingTier={() => onAddBillingTier('privateInventorySpendTiers')}
                        onDeleteBillingTier={tierId =>
                            onDeleteBillingTier({
                                type: 'privateInventorySpendTiers',
                                tierId,
                            })
                        }
                        onEditBillingTierMax={({ tierId, value }) => {
                            onEditBillingTierMax({
                                type: 'privateInventorySpendTiers',
                                tierId,
                                value,
                            });
                        }}
                        onEditBillingTierFee={({ tierId, value }) =>
                            onEditBillingTierFee({
                                type: 'privateInventorySpendTiers',
                                tierId,
                                value,
                            })
                        }
                        canEditRestrictedFields={canEditRestrictedFields}
                        showErrors={showErrors}
                        isEditingSelf={isEditingSelf}
                        type="privateInventorySpendTiers"
                        errors={errors}
                    />
                </Box>
            </Box>
            <Box>
                <Typography gutterBottom variant="h6">
                    Public Inventory Fees
                </Typography>
                {!isEditingSelf && (
                    <React.Fragment>
                        <Box mb={3}>
                            <FormLabel
                                required
                                classes={{
                                    asterisk: classes.red,
                                }}
                            >
                                Infrastructure Cost
                            </FormLabel>
                            <FormField isRequired={true}>
                                <TextFieldFormatter
                                    initialValue={draft.publicInventoryInfraCost}
                                    onChange={value => onChange('publicInventoryInfraCost', value)}
                                    shouldAllowChange={v => !/[a-zA-Z]/.test(v)}
                                    formatIn={v => {
                                        return isNil(v) ? '' : numeral(v).format('0,0.00');
                                    }}
                                    formatOut={v => {
                                        return v ? numeral(v).value() : 0;
                                    }}
                                    renderInput={({ value, onChange }) => (
                                        <TextField
                                            value={value}
                                            onChange={onChange}
                                            required
                                            disabled={!canEditRestrictedFields}
                                            helperText={
                                                showErrors && errors.publicInventoryInfraCost
                                            }
                                            error={showErrors && errors.publicInventoryInfraCost}
                                            endAdornment="CPM"
                                            startAdornment={environmentSettings.getCurrencySign()}
                                        />
                                    )}
                                />
                            </FormField>
                        </Box>
                        <Box mb={3}>
                            <FormLabel
                                required
                                classes={{
                                    asterisk: classes.red,
                                }}
                            >
                                Infrastructure Cost Percentage
                            </FormLabel>
                            <FormField isRequired={true}>
                                <TextFieldFormatter
                                    initialValue={draft.infraCostPercentage}
                                    onChange={value => onChange('infraCostPercentage', value)}
                                    shouldAllowChange={v => !/[a-zA-Z]/.test(v)}
                                    formatIn={v => {
                                        return isNil(v)
                                            ? '0'
                                            : numeral(v)
                                                  .multiply(100)
                                                  .format('0.00');
                                    }}
                                    formatOut={v => {
                                        return v ? numeral(v).value() / 100 : 0;
                                    }}
                                    renderInput={({ value, onChange }) => (
                                        <TextField
                                            value={value}
                                            onChange={onChange}
                                            required
                                            disabled={!canEditRestrictedFields}
                                            helperText={showErrors && errors.infraCostPercentage}
                                            error={showErrors && errors.infraCostPercentage}
                                            endAdornment=" % "
                                        />
                                    )}
                                />
                            </FormField>
                        </Box>
                    </React.Fragment>
                )}
                <Box>
                    <FormLabel
                        required
                        classes={{
                            asterisk: classes.red,
                        }}
                    >
                        Tiers
                    </FormLabel>
                    <BillingTiers
                        tiers={draft.publicInventorySpendTiers}
                        onAddBillingTier={() => onAddBillingTier('publicInventorySpendTiers')}
                        onDeleteBillingTier={tierId =>
                            onDeleteBillingTier({
                                type: 'publicInventorySpendTiers',
                                tierId,
                            })
                        }
                        onEditBillingTierMax={({ tierId, value }) => {
                            onEditBillingTierMax({
                                type: 'publicInventorySpendTiers',
                                tierId,
                                value,
                            });
                        }}
                        onEditBillingTierFee={({ tierId, value }) =>
                            onEditBillingTierFee({
                                type: 'publicInventorySpendTiers',
                                tierId,
                                value,
                            })
                        }
                        canEditRestrictedFields={canEditRestrictedFields}
                        showErrors={showErrors}
                        isEditingSelf={isEditingSelf}
                        type="publicInventorySpendTiers"
                        errors={errors}
                    />
                </Box>
            </Box>

            {/* Tier Calculation Status Section */}
            {useSpendingCycle && mode === 'edit' && draft.type === 'media_buyer' && (
                <TierCalculationStatus
                    tierCalculation={draft.latestTierCalculation}
                    name={draft.name}
                    isInternalUser={isInternalUser()}
                />
            )}
        </div>
    );
};

export default connect(selector)(Fees);
